import actionCreatorFactory from "../util/redux/action-factory";
import { reducerWithInitialState } from "../util/redux/typed-reducers";

import * as request from "../util/request";
import { PortfolioDto } from "../util/DTOs/PortfolioDtos";
import { ReduxState, ReduxDispatch } from "./reducers";
import { env } from "../util/env";
import { showSnackbar } from "./snackbar";
import { DialogType } from "../components/Dialog/KycDialog";

/**
 * Action factory + actions
 */
const actionFactory = actionCreatorFactory("HypoMobiili/Portfolio");
const getPortfolioAction = actionFactory<undefined, PortfolioDto, string>(
    "GET_PORTFOLIO"
);

const setKycDialogOpenAction = actionFactory<undefined, boolean, string>(
    "SET_KYC_DIALOG_OPEN"
);

const setKycDialogContentAction = actionFactory<
    undefined,
    KycDialogContent,
    string
>("SET_KYC_DIALOG_CONTENT");

export function setKycDialogState(state: boolean) {
    return async (dispatch: ReduxDispatch) => {
        dispatch(setKycDialogOpenAction(undefined));
        try {
            dispatch(setKycDialogOpenAction.done(state));
        } catch (error) {
            dispatch(
                setKycDialogOpenAction.failed("Setting dialog state failed")
            );
            throw error;
        }
    };
}

export function setKycDialogContent(content: KycDialogContent) {
    return async (dispatch: ReduxDispatch) => {
        dispatch(setKycDialogContentAction(undefined));
        try {
            dispatch(setKycDialogContentAction.done(content));
        } catch (error) {
            dispatch(
                setKycDialogContentAction.failed(
                    "Setting dialog content failed"
                )
            );
            throw error;
        }
    };
}

export function getPortfolio() {
    return async (dispatch: ReduxDispatch) => {
        dispatch(getPortfolioAction(undefined));

        try {
            const result = await request.get<PortfolioDto>(
                `${env.APP_API_HOST}/api/portfolio`
            );

            dispatch(getPortfolioAction.done(result));
            return result;
        } catch (error) {
            dispatch(getPortfolioAction.failed("Failed to load portfolio"));
            if (error.status !== 401) {
                dispatch(
                    showSnackbar("Varallisuussalkun lataaminen epäonnistui")
                );
            }
            throw error;
        }
    };
}

export interface KycDialogContent {
    title: string;
    content: string[];
    type: DialogType;
}

export interface KycDialogState extends KycDialogContent {
    open?: boolean;
}

/**
 * The internal state of this reducer
 */
export interface PortfolioState extends PortfolioDto {
    loading: boolean;
    kycDialogState: KycDialogState;
}

/**
 * The reducer.
 *
 * The module exports this as default
 */

const initialPortfolioState: PortfolioState = {
    accounts: [],
    creditCards: [],
    loans: [],
    loading: false,
    kycDialogState: {
        title: "",
        content: [],
        type: DialogType.Optional,
    },
};

export const PortfolioReducer = reducerWithInitialState(initialPortfolioState)
    .case(getPortfolioAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(getPortfolioAction.done, (state, action) => {
        return {
            ...state,
            ...action.payload,
            loading: false,
        };
    })
    .case(getPortfolioAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(setKycDialogOpenAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(setKycDialogOpenAction.done, (state, action) => {
        return {
            ...state,
            loading: false,
            kycDialogState: {
                ...state.kycDialogState,
                open: action.payload,
            },
        };
    })
    .case(setKycDialogOpenAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(setKycDialogContentAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(setKycDialogContentAction.done, (state, action) => {
        return {
            ...state,
            loading: false,
            kycDialogState: {
                open: state.kycDialogState.open,
                ...action.payload,
            },
        };
    })
    .case(setKycDialogContentAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    });

export default PortfolioReducer;

/**
 * Selectors
 */
export const getPortfolioData = (state: ReduxState) => {
    return state && state.portfolio;
};



// WEBPACK FOOTER //
// ./src/ducks/portfolio.ts